import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { GoogleReviewsBadge } from "@/components/shared/GoogleReviewsBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, Phone, Mail, ArrowRight, MessageCircle, Clock, CheckCircle } from "lucide-react";
import { useState, useEffect, lazy } from "react";
import { useRevealObserver } from "@/hooks/useRevealObserver";
import { toast } from "sonner";
import { getUTMData } from "@/lib/utm";
import { submitLead } from "@/lib/leadWebhook";
import { formTracker } from "@/lib/formTracking";
import { SEOHead } from "@/features/shared/SEOHead";
import { generateProfessionalServiceSchema } from "@/lib/business-schema";
import { Link } from "react-router-dom";
import { ReviewWidgetSection } from "@/components/ReviewWidgetSection";



const Contact = () => {
  useRevealObserver();

  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    interested_solution: "",
    message: ""
  });
  const [consentResponse, setConsentResponse] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  // Set meta tags for Contact page




  // Initialize form tracking
  useEffect(() => {
    const utmData = getUTMData();
    formTracker.initForm('contact_form', 'contact_page', 7, utmData);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Track field interaction
    formTracker.trackFieldInteraction('contact_form', name, e.target.type);

    // Track field completion if value is not empty
    if (value.trim()) {
      formTracker.trackFieldCompletion('contact_form', name, value);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.first_name || !formData.last_name || !formData.email || !formData.message) {
      toast.error("Error", {
        description: "Please fill in all required fields",
      });
      return;
    }

    if (!consentResponse) {
      toast.error("Consent Required", {
        description: "Please consent to receive responses to your inquiry.",
      });
      return;
    }

    setIsLoading(true);

    // Track form submission
    if (formData.interested_solution !== 'vendor-pitch') formTracker.trackFormSubmit('contact_form', true);

    try {
      // Vendor pitches still reach the inbox, under their own source and without lead
      // conversion events, so they stay out of the sales pipeline and the ad/GA4 lead counts.
      const isVendor = formData.interested_solution === 'vendor-pitch';

      // Send to the single Frayze lead webhook (full canonical schema, every key every time)
      const response = await submitLead(isVendor ? 'vendor_pitch' : 'contact_page', {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim(),
        interested_solution: formData.interested_solution,
        message: formData.message.trim(),
        form_location: 'contact_page',
        consent_response: consentResponse ? 'yes' : 'no',
        consent_marketing: consentMarketing ? 'yes' : 'no',
      }, { conversion: !isVendor });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // GTM Event Tracking - Form Submit Success
      if (!isVendor && typeof window !== 'undefined' && window.dataLayer) {
        const utmData = getUTMData();
        window.dataLayer.push({
          event: 'form_submit',
          form_id: 'contact_form',
          form_location: 'contact_page',
          interested_solution: formData.interested_solution,
          page_location: window.location.href,
          utm_source: utmData.utm_source,
          utm_medium: utmData.utm_medium,
          utm_campaign: utmData.utm_campaign,
          utm_term: utmData.utm_term,
          utm_content: utmData.utm_content,
          referrer: utmData.referrer
        });

        // Also trigger lead event
        window.dataLayer.push({
          event: 'qualified_lead',
          lead_source: 'contact_form',
          lead_type: 'contact_inquiry',
          interested_solution: formData.interested_solution,
          utm_source: utmData.utm_source,
          utm_medium: utmData.utm_medium,
          utm_campaign: utmData.utm_campaign,
          utm_term: utmData.utm_term,
          utm_content: utmData.utm_content,
          referrer: utmData.referrer
        });
      }

      // Track affiliate lead if affiliate manager is available
      const win = window as unknown as { affiliateManager?: { trackLead: (data: unknown, cb: () => void) => void } };
      if (typeof window !== 'undefined' && win.affiliateManager) {
        try {
          win.affiliateManager.trackLead(
            {
              firstName: formData.first_name,
              lastName: formData.last_name,
              email: formData.email,
            },
            function () {
              // Lead tracked successfully
            }
          );
        } catch {
          // Silently handle affiliate tracking errors
        }
      }

      toast.success("Message Sent!", {
        description: "We've received your message and will get back to you within 2 hours.",
      });

      // Reset form
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        company: "",
        interested_solution: "",
        message: ""
      });
      setConsentResponse(false);
      setConsentMarketing(false);
    } catch (error: unknown) {
      console.error('Contact form error:', error);
      toast.error("Error", {
        description: "Failed to send message. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const solutions = [
    { name: "CRM & Growth System", value: "crm-growth" },
    { name: "Lead Gen & Marketing", value: "lead-gen" },
    { name: "Web & SEO", value: "web-seo" },
    { name: "Custom AI Solutions", value: "custom-ai" },
    { name: "AI Strategy", value: "ai-strategy" },
    { name: "Not sure / Full Growth Plan", value: "growth-plan" },
    { name: "Custom Package", value: "custom" },
    { name: "I'm offering a product or service to Frayze", value: "vendor-pitch" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SEOHead
        title="Contact Frayze | Thunder Bay Web Design & CRM Experts"
        description="Contact Canada's leading digital agency for CRM, automation, and AI solutions. Serving Ontario & North America. Get your free consultation today."
        keywords="contact Canadian digital agency, CRM consultation, lead generation help, web development contact, AI solutions Canada, North American business consultation"
        ogTitle="Contact Frayze | Thunder Bay Web Design & CRM Experts"
        ogDescription="Contact Canada's leading digital agency for CRM, automation, and AI solutions. Serving Ontario & North America. Get your free consultation today."
        twitterTitle="Contact Frayze | Thunder Bay Web Design & CRM Experts"
        twitterDescription="Contact Canada's leading digital agency for CRM, automation, and AI solutions. Serving Ontario & North America. Get your free consultation today."
        canonical="https://frayze.ca/contact/"
        robots="index, follow, max-snippet:-1, max-image-preview:large"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ContactPage",
              "@id": "https://frayze.ca/contact",
              "name": "Contact Frayze Technologies Inc.",
              "description": "Get in touch with Canada's leading digital agency for CRM, lead generation, web development, and AI solutions.",
              "url": "https://frayze.ca/contact",
              "mainEntity": {
                "@type": "Organization",
                "@id": "https://frayze.ca/#organization"
              },
              "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://frayze.ca"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Contact",
                    "item": "https://frayze.ca/contact"
                  }
                ]
              }
            },
            generateProfessionalServiceSchema()
          ]
        }}
      />

      <main className="pt-40 md:pt-48 pb-20">
        {/* 1. Hero Section (Short & Clean) */}
        <section className="container mx-auto px-6 text-center mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Speak With Our Team
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Tell us your goal and we&apos;ll show you the fastest path to more leads. We reply within 2 hours.
          </p>
          <div className="flex justify-center mb-8">
            <GoogleReviewsBadge />
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              variant="default"
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-8 py-6 font-bold uppercase"
              onClick={() => window.location.href = '/book-consultation/'}
            >
              Book My Free Strategy Call
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground text-lg px-8 py-6 font-bold uppercase"
              asChild
            >
              <a href="tel:+18077000079">
                Call Now: +1 807-700-0079
              </a>
            </Button>
          </div>
        </section>

        {/* 2. Contact Options (Simple 3-Column) */}
        <section className="container mx-auto px-6 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="reveal lift-hover text-center hover:shadow-md transition-all">
              <CardContent className="p-6">
                <Phone className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold mb-1">Call Us</h3>
                <a href="tel:+18077000079" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  +1 807-700-0079
                </a>
              </CardContent>
            </Card>
            <Card className="reveal reveal-delay-1 lift-hover text-center hover:shadow-md transition-all">
              <CardContent className="p-6">
                <Mail className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold mb-1">Email Us</h3>
                <a href="mailto:hello@frayze.ca" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  hello@frayze.ca
                </a>
              </CardContent>
            </Card>
            <Card className="reveal reveal-delay-2 lift-hover text-center hover:shadow-md transition-all">
              <CardContent className="p-6">
                <MessageCircle className="h-8 w-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold mb-1">Message Us</h3>
                <a href="https://wa.me/18077000079" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  WhatsApp Chat
                </a>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* SOCIAL PROOF WIDGET */}
        <section className="container mx-auto px-6 mb-20">
          <div className="max-w-4xl mx-auto">
            <ReviewWidgetSection />
          </div>
        </section>

        {/* 3. Contact Form & Office Info */}
        <section id="contact-form" className="container mx-auto px-6 mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-6xl mx-auto">

            {/* Form Column */}
            <div className="lg:col-span-7">
              <Card>
                <CardHeader>
                  <CardTitle>Send us a Message</CardTitle>
                  <CardDescription>
                    Tell us what you need and get a clear next step. We reply within 2 hours during business hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-first_name" className="block text-sm font-medium mb-1.5">First Name*</label>
                        <input
                          type="text"
                          id="contact-first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          required
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-last_name" className="block text-sm font-medium mb-1.5">Last Name*</label>
                        <input
                          type="text"
                          id="contact-last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          required
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium mb-1.5">Email*</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-phone" className="block text-sm font-medium mb-1.5">Phone</label>
                        <input
                          type="tel"
                          id="contact-phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-company" className="block text-sm font-medium mb-1.5">Company</label>
                        <input
                          type="text"
                          id="contact-company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-interested_solution" className="block text-sm font-medium mb-1.5">Interested Solution</label>
                        <select
                          id="contact-interested_solution"
                          name="interested_solution"
                          value={formData.interested_solution}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        >
                          <option value="">Select a solution...</option>
                          {solutions.map((solution) => (
                            <option key={solution.value} value={solution.value}>
                              {solution.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium mb-1.5">Message*</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-vertical"
                        placeholder="How can we help you?"
                      ></textarea>
                    </div>

                    {/* A2P Compliant Dual Opt-In Checkboxes */}
                    <div className="space-y-3 border-t pt-3">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="consent-response"
                          checked={consentResponse}
                          onCheckedChange={(checked) => setConsentResponse(checked as boolean)}
                          className="mt-1"
                          required
                        />
                        <label htmlFor="consent-response" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                          I consent to receive responses to my inquiry via phone, email, or SMS. Message frequency may vary. Message & Data rates may apply. Reply HELP for help or STOP to opt-out at anytime. *
                        </label>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="consent-marketing"
                          checked={consentMarketing}
                          onCheckedChange={(checked) => setConsentMarketing(checked as boolean)}
                          className="mt-1"
                        />
                        <label htmlFor="consent-marketing" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                          I consent to receive marketing materials, updates, and promotional content via phone, email, or SMS. Message frequency may vary. Message & Data rates may apply. Reply STOP to opt-out at anytime.
                        </label>
                      </div>
                    </div>

                    <Button type="submit" className="w-full" size="lg" disabled={isLoading || !consentResponse}>
                      {isLoading ? "Sending..." : "Send Message"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Info Column */}
            <div className="lg:col-span-5 space-y-8">
              {/* Office Info */}
              <div>
                <h3 className="font-bold text-lg mb-4">Office Information</h3>
                <div className="space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Frayze Technologies Inc.</p>
                      <p className="text-muted-foreground">202 Franklin St. North</p>
                      <p className="text-muted-foreground">Thunder Bay, ON P7C 4H9</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Business Hours</p>
                      <p className="text-muted-foreground">Mon - Fri: 9 AM - 6 PM EST</p>
                      <p className="text-muted-foreground">AI Support: 24/7</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-lg overflow-hidden border border-border/50 h-[200px] bg-muted/20">
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight={0}
                  marginWidth={0}
                  src="https://maps.google.com/maps?q=202%20Franklin%20St.%20North%2C%20Thunder%20Bay%2C%20ON%20P7C%204H9&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  title="Frayze Technologies Location"
                  className="w-full h-full"
                ></iframe>
              </div>

              {/* Light Social Proof */}
              <div>
                <h3 className="font-bold text-lg mb-4">Trusted By</h3>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="py-1.5 px-3">
                    <CheckCircle className="w-3 h-3 mr-1.5 text-green-500" />
                    5/5 Google Reviews
                  </Badge>
                  <Badge variant="outline" className="py-1.5 px-3">
                    <CheckCircle className="w-3 h-3 mr-1.5 text-green-500" />
                    15+ Canadian Clients
                  </Badge>
                </div>
              </div>

              {/* AI Demo Micro-CTA */}
              <div className="bg-muted/30 p-4 rounded-lg border border-border/50">
                <p className="text-sm font-medium mb-1">Want to test our AI?</p>
                <p className="text-xs text-muted-foreground mb-2">Try our AI assistant anytime.</p>
                <a href="tel:+14374949095" className="text-sm font-bold text-primary hover:underline flex items-center">
                  Call (437) 494-9095 <ArrowRight className="ml-1 h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Internal Links Block (SEO) */}
        <section className="container mx-auto px-6 py-12 border-t border-border/50">
          <h2 className="text-xl font-bold mb-6">Looking for more information?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
            <Link to="/pricing/" className="text-muted-foreground hover:text-primary transition-colors">
              View Pricing
            </Link>
            <Link to="/case-studies/" className="text-muted-foreground hover:text-primary transition-colors">
              Read Case Studies
            </Link>
            <Link to="/solutions/" className="text-muted-foreground hover:text-primary transition-colors">
              Explore Solutions
            </Link>
            <Link to="/solutions/" className="text-muted-foreground hover:text-primary transition-colors">
              Browse Industries
            </Link>
            <Link to="/service-areas/thunder-bay/" className="text-muted-foreground hover:text-primary transition-colors">
              Thunder Bay Services
            </Link>
            <Link to="/digital-audit/" className="text-muted-foreground hover:text-primary transition-colors">
              Free Digital Audit
            </Link>
          </div>
        </section>
      </main>

      <Footer />


    </div>
  );
};

export default Contact;